"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DerivedRouteSeoMetadataSchema = void 0;
exports.deriveRouteSeoMetadata = deriveRouteSeoMetadata;
const zod_1 = require("zod");
const external_video_1 = require("./external-video");
const formula_evaluator_1 = require("./formula-evaluator");
const kb_article_blocks_1 = require("./kb-article-blocks");
const publication_contract_1 = require("./publication-contract");
const DESCRIPTION_MAX_LENGTH = 160;
exports.DerivedRouteSeoMetadataSchema = zod_1.z
    .object({
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().min(1).nullable(),
    canonical: zod_1.z.string().url().nullable(),
    canonicalSource: zod_1.z.enum(['explicit', 'auto', 'none']),
    hreflang: zod_1.z.array(zod_1.z.string().min(1)),
    noindex: zod_1.z.boolean(),
    robotsDirectives: zod_1.z.array(zod_1.z.string().min(1)),
    ogImage: zod_1.z.string().min(1).nullable(),
    ogType: zod_1.z.enum(['website', 'article']),
    publishedAt: zod_1.z.string().min(1).nullable(),
    modifiedAt: zod_1.z.string().min(1).nullable(),
    structuredData: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
})
    .strict();
function deriveRouteSeoMetadata(input) {
    const row = input.source?.row ?? null;
    const rowValues = input.source && row ? materializeRowValues(input.source, row) : {};
    const title = readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.TITLE) ??
        resolvePageTitle(input.page, rowValues) ??
        cleanText(input.appTitle) ??
        (input.page
            ? input.page.path === '/'
                ? 'Startseite'
                : titleFromRoute(input.page.path, input.page.id)
            : null) ??
        cleanText(input.branding?.displayName) ??
        titleFromRoute(input.route.path, input.route.pageId);
    const description = readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.DESCRIPTION) ??
        readNamedText(input.source, ['summary', 'excerpt']) ??
        excerptText(readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.BODY)) ??
        cleanText(input.appDescription);
    const explicitCanonical = validAbsoluteUrl(row?.publication?.canonical) ??
        validAbsoluteUrl(readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.CANONICAL));
    const canonical = explicitCanonical ??
        buildRouteCanonicalUrl(input.publicBaseUrl, input.route.path);
    const canonicalSource = explicitCanonical
        ? 'explicit'
        : canonical
            ? 'auto'
            : 'none';
    const noindex = shouldNoindex(input, row);
    const ogImage = readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.IMAGE) ??
        cleanText(input.branding?.socialImageUrl) ??
        cleanText(input.branding?.logoUrl);
    const ogType = input.route.rowId ? 'article' : 'website';
    const publishedAt = row?.publication?.publishedAt ?? null;
    const modifiedAt = readNamedText(input.source, ['updatedAt', 'modifiedAt', 'updated']) ??
        publishedAt ??
        null;
    const structuredData = buildStructuredData({
        title,
        description,
        canonical,
        locale: input.route.locale,
        ogImage,
        ogType,
        publishedAt,
        modifiedAt,
        path: input.route.path,
        publicBaseUrl: input.publicBaseUrl ?? null,
        displayName: cleanText(input.branding?.displayName) ??
            cleanText(input.appTitle) ??
            title,
        logoUrl: cleanText(input.branding?.logoUrl),
        authors: normalizeAuthors(input.authors),
        videos: readStructuredArticleVideos(rowValues),
    });
    return exports.DerivedRouteSeoMetadataSchema.parse({
        title,
        description,
        canonical,
        canonicalSource,
        hreflang: parseHreflangGroup(row?.publication?.hreflangGroup ??
            readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.HREFLANG_GROUP)),
        noindex,
        robotsDirectives: noindex ? ['noindex', 'nofollow'] : ['index', 'follow'],
        ogImage,
        ogType,
        publishedAt,
        modifiedAt,
        structuredData,
    });
}
function materializeRowValues(source, row) {
    const values = { id: row.id };
    const publication = (0, publication_contract_1.createPublicationColumnIndex)(source.columns);
    for (const column of source.columns) {
        values[column.name] = row.cells[column.id] ?? row.cells[column.name];
    }
    for (const [semanticKey, column] of Object.entries(publication)) {
        if (column)
            values[semanticKey] = row.cells[column.id];
    }
    return values;
}
function resolvePageTitle(page, rowValues) {
    if (!page)
        return null;
    if (page.titleFormula) {
        try {
            const value = (0, formula_evaluator_1.evaluateFormula)(page.titleFormula, {
                data: {
                    page,
                    row: rowValues,
                },
            });
            const text = cleanText(value);
            if (text)
                return text;
        }
        catch {
            // SEO derivation must fall through to deterministic route/page labels.
        }
    }
    return null;
}
function readPublicationText(source, field) {
    const row = source?.row;
    if (!source || !row)
        return null;
    const column = (0, publication_contract_1.createPublicationColumnIndex)(source.columns)[field];
    const value = column ? row.cells[column.id] : undefined;
    return cleanPublicationText(field, value);
}
function readNamedText(source, names) {
    const row = source?.row;
    if (!source || !row)
        return null;
    for (const name of names) {
        const value = row.cells[name] ?? readByColumnName(source, row, name);
        const text = cleanText(value);
        if (text)
            return text;
    }
    return null;
}
function readByColumnName(source, row, columnName) {
    const normalized = columnName.toLowerCase();
    const column = source.columns.find(item => item.name.trim().toLowerCase() === normalized);
    return column ? row.cells[column.id] : undefined;
}
function cleanPublicationText(field, value) {
    if (field !== publication_contract_1.PUBLICATION_COLUMN_KEYS.BODY)
        return cleanText(value);
    const document = (0, kb_article_blocks_1.safeParseKbArticleBlockDocument)(value);
    if (document && document.blocks.length > 0) {
        return cleanText((0, kb_article_blocks_1.extractKbArticlePlainText)(document));
    }
    return cleanText(value);
}
function cleanText(value) {
    if (value === null || value === undefined)
        return null;
    if (Array.isArray(value)) {
        for (const item of value) {
            const text = cleanText(item);
            if (text)
                return text;
        }
        return null;
    }
    if (typeof value === 'object') {
        const record = value;
        return (cleanText(record['text']) ??
            cleanText(record['title']) ??
            cleanText(record['url']) ??
            cleanText(record['src']) ??
            cleanText(record['value']));
    }
    const stripped = String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ');
    const text = stripped.trim();
    return text || null;
}
function excerptText(value) {
    if (!value)
        return null;
    return value.length > DESCRIPTION_MAX_LENGTH
        ? `${value.slice(0, DESCRIPTION_MAX_LENGTH - 3).trimEnd()}...`
        : value;
}
function validAbsoluteUrl(value) {
    const text = cleanText(value);
    if (!text)
        return null;
    try {
        const url = new URL(text);
        return url.protocol === 'http:' || url.protocol === 'https:'
            ? url.toString()
            : null;
    }
    catch {
        return null;
    }
}
function buildRouteCanonicalUrl(publicBaseUrl, path) {
    const base = validAbsoluteUrl(publicBaseUrl);
    if (!base)
        return null;
    return appendRoutePathToBase(base, path);
}
function shouldNoindex(input, row) {
    if (input.page?.audience === 'internal')
        return true;
    if (input.page?.audience === 'portal' || input.page?.audience === 'members') {
        return true;
    }
    if (input.page?.seoIndexing === false)
        return true;
    if (input.page?.status &&
        publication_contract_1.NON_PUBLIC_PUBLICATION_STATUSES.includes(input.page.status)) {
        return true;
    }
    if (row?.publication?.noindex === true)
        return true;
    const explicitNoindex = readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.NOINDEX);
    if (explicitNoindex && ['true', '1', 'yes', 'ja'].includes(explicitNoindex)) {
        return true;
    }
    const status = row?.publication?.status ??
        readPublicationText(input.source, publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS);
    const parsedStatus = publication_contract_1.PublicationStatusSchema.safeParse(status);
    return parsedStatus.success
        ? !publication_contract_1.PUBLISHABLE_PUBLICATION_STATUSES.includes(parsedStatus.data)
        : false;
}
function parseHreflangGroup(value) {
    if (!value)
        return [];
    return [
        ...new Set(value
            .split(/[,\s]+/)
            .map(tag => tag.trim())
            .filter(Boolean)),
    ];
}
function normalizeAuthors(authors) {
    if (!authors)
        return [];
    const normalized = [];
    const seen = new Set();
    for (const author of authors) {
        const name = cleanText(author.name);
        if (!name)
            continue;
        const key = name.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        normalized.push({
            name,
            url: validAbsoluteUrl(author.url),
            image: validAbsoluteUrl(author.image) ?? cleanText(author.image),
            type: author.type ?? 'Person',
        });
    }
    return normalized;
}
function titleFromRoute(path, fallback) {
    const lastSegment = path.split('/').filter(Boolean).at(-1);
    const candidate = lastSegment ?? fallback;
    return candidate
        .replace(/^:/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^\w/, char => char.toUpperCase());
}
function buildStructuredData(input) {
    const siteBaseUrl = resolveSiteBaseUrl(input.publicBaseUrl, input.canonical);
    const organizationId = siteBaseUrl ? `${siteBaseUrl}#organization` : null;
    const websiteId = siteBaseUrl ? `${siteBaseUrl}#website` : null;
    const primaryId = input.canonical
        ? `${input.canonical}#${input.ogType === 'article' ? 'article' : 'webpage'}`
        : null;
    const primary = {
        '@type': input.ogType === 'article' ? 'Article' : 'WebPage',
        headline: input.title,
        name: input.title,
        inLanguage: input.locale,
    };
    if (primaryId)
        primary['@id'] = primaryId;
    if (input.description)
        primary['description'] = input.description;
    if (input.canonical) {
        primary['url'] = input.canonical;
        primary['mainEntityOfPage'] = input.canonical;
    }
    if (input.ogImage)
        primary['image'] = input.ogImage;
    if (input.publishedAt)
        primary['datePublished'] = input.publishedAt;
    if (input.modifiedAt)
        primary['dateModified'] = input.modifiedAt;
    if (websiteId)
        primary['isPartOf'] = { '@id': websiteId };
    if (organizationId)
        primary['publisher'] = { '@id': organizationId };
    if (input.ogType === 'article' && input.authors.length > 0) {
        primary['author'] = input.authors.map(author => {
            const entity = {
                '@type': author.type ?? 'Person',
                name: author.name,
            };
            if (author.url)
                entity['url'] = author.url;
            if (author.image)
                entity['image'] = author.image;
            return entity;
        });
    }
    if (input.videos.length > 0) {
        primary['hasPart'] = input.videos.map(video => ({
            '@type': 'VideoObject',
            name: video.name,
            ...(video.description ? { description: video.description } : {}),
            embedUrl: video.embedUrl,
            contentUrl: video.contentUrl,
            ...(video.thumbnailUrl ? { thumbnailUrl: video.thumbnailUrl } : {}),
        }));
    }
    const graph = [];
    if (organizationId && siteBaseUrl) {
        const organization = {
            '@type': 'Organization',
            '@id': organizationId,
            name: input.displayName,
            url: siteBaseUrl,
        };
        if (input.logoUrl)
            organization['logo'] = input.logoUrl;
        graph.push(organization);
    }
    if (websiteId && siteBaseUrl) {
        graph.push({
            '@type': 'WebSite',
            '@id': websiteId,
            name: input.displayName,
            url: siteBaseUrl,
            ...(organizationId ? { publisher: { '@id': organizationId } } : {}),
            potentialAction: {
                '@type': 'SearchAction',
                target: buildSearchActionTarget(siteBaseUrl, input.path),
                'query-input': 'required name=search_term_string',
            },
        });
    }
    const breadcrumb = buildBreadcrumbStructuredData(input, siteBaseUrl);
    if (breadcrumb)
        graph.push(breadcrumb);
    graph.push(primary);
    return {
        '@context': 'https://schema.org',
        '@graph': graph,
    };
}
function readStructuredArticleVideos(rowValues) {
    const document = (0, kb_article_blocks_1.safeParseKbArticleBlockDocument)(rowValues['blocks']);
    if (!document)
        return [];
    return document.blocks
        .filter((block) => block.type === 'embed_video' && block.enabled !== false)
        .map(block => ({
        name: block.title,
        description: cleanText(block.caption),
        embedUrl: (0, external_video_1.externalVideoEmbedUrl)(block),
        contentUrl: (0, external_video_1.externalVideoWatchUrl)(block),
        thumbnailUrl: typeof block.poster === 'string'
            ? validAbsoluteUrl(block.poster)
            : null,
    }));
}
function buildSearchActionTarget(siteBaseUrl, path) {
    const firstSegment = path.split('/').filter(Boolean)[0];
    const searchUrl = appendRoutePathToBase(siteBaseUrl, firstSegment ?? '');
    return `${searchUrl}${searchUrl.includes('?') ? '&' : '?'}q={search_term_string}`;
}
function resolveSiteBaseUrl(publicBaseUrl, canonical) {
    const base = validAbsoluteUrl(publicBaseUrl);
    if (base)
        return normalizePublicBaseUrl(base);
    const url = validAbsoluteUrl(canonical);
    if (!url)
        return null;
    const parsed = new URL(url);
    return `${parsed.origin}/`;
}
function normalizePublicBaseUrl(baseUrl) {
    const parsed = new URL(baseUrl);
    parsed.search = '';
    parsed.hash = '';
    parsed.pathname = normalizeUrlPath(parsed.pathname);
    return parsed.toString();
}
function appendRoutePathToBase(baseUrl, routePath) {
    try {
        const parsed = new URL(baseUrl);
        parsed.search = '';
        parsed.hash = '';
        const basePath = normalizeUrlPath(parsed.pathname).replace(/\/+$/, '');
        const suffix = routePath.replace(/^\/+/, '').replace(/\/+$/, '');
        parsed.pathname = normalizeUrlPath([basePath, suffix].filter(Boolean).join('/'));
        return parsed.toString();
    }
    catch {
        return baseUrl;
    }
}
function normalizeUrlPath(pathname) {
    const trimmed = pathname.replace(/\/+$/, '');
    return trimmed ? `/${trimmed.replace(/^\/+/, '')}` : '/';
}
function buildBreadcrumbStructuredData(input, siteBaseUrl) {
    if (!siteBaseUrl)
        return null;
    const segments = input.path.split('/').filter(Boolean);
    const items = [
        {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteBaseUrl,
        },
    ];
    let currentPath = '';
    segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const isLast = index === segments.length - 1;
        items.push({
            '@type': 'ListItem',
            position: index + 2,
            name: isLast ? input.title : titleFromRoute(segment, segment),
            item: isLast && input.canonical
                ? input.canonical
                : appendRoutePathToBase(siteBaseUrl, currentPath),
        });
    });
    return {
        '@type': 'BreadcrumbList',
        itemListElement: items,
    };
}
